import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import Loader from './components/Loader';
import {
    ISearchAttempt,
    ISearchResult,
    formatDateTime,
    getEngineColor
} from './types/adminDebug';
import './ReportSourcesView.css';

const AdminSearchAttemptDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [attempt, setAttempt] = useState<ISearchAttempt | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showRawResponse, setShowRawResponse] = useState(false);

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Fetch search attempt details
    const fetchAttempt = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/search-attempts/${id}`, {
                headers
            });

            if (!response.ok) {
                throw new Error('Failed to fetch search attempt');
            }

            const data = await response.json();
            setAttempt(data);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        if (authLoaded && id) {
            fetchAttempt();
        }
    }, [authLoaded, id, fetchAttempt]);

    const handleBack = () => {
        navigate(-1);
    };

    if (!authLoaded || loading) {
        return <Loader text={t('common.loading')} />;
    }

    if (error) {
        return (
            <div className="container">
                <div className="error-message" style={{ color: 'red' }}>
                    {error}
                </div>
                <button onClick={handleBack} style={{ marginTop: '1rem' }}>
                    {t('adminDebug.backToList')}
                </button>
            </div>
        );
    }

    if (!attempt) {
        return (
            <div className="container">
                <div className="no-results">{t('adminDebug.searchAttempts.notFound')}</div>
                <button onClick={handleBack} style={{ marginTop: '1rem' }}>
                    {t('adminDebug.backToList')}
                </button>
            </div>
        );
    }

    const labelStyle: React.CSSProperties = {
        textAlign: 'right',
        paddingRight: '15px',
        fontWeight: 'bold',
        color: '#666'
    };

    const valueStyle: React.CSSProperties = {
        textAlign: 'left',
        wordBreak: 'break-word'
    };

    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: '180px auto',
        columnGap: '15px',
        rowGap: '12px',
        alignItems: 'baseline'
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: '2rem',
        padding: '1.5rem',
        backgroundColor: 'var(--card-bg, #f9f9f9)',
        borderRadius: '8px',
        border: '1px solid var(--border-color, #e0e0e0)'
    };

    return (
        <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h1>{t('adminDebug.searchAttempts.detailTitle')}</h1>
                <button
                    onClick={handleBack}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                    }}
                >
                    {t('adminDebug.backToList')}
                </button>
            </div>

            {/* Basic Information */}
            <div style={sectionStyle}>
                <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.basicInfo')}</h2>
                <div style={gridStyle}>
                    <div style={labelStyle}>{t('adminDebug.fields.id')}:</div>
                    <div style={valueStyle}>{attempt._id}</div>

                    <div style={labelStyle}>{t('adminDebug.fields.searchTargetId')}:</div>
                    <div style={valueStyle}>
                        <Link
                            to={`/admin/search-targets/${attempt.search_target_id}`}
                            style={{ color: '#007bff' }}
                        >
                            {attempt.search_target_id}
                        </Link>
                    </div>

                    <div style={labelStyle}>{t('adminDebug.fields.query')}:</div>
                    <div style={valueStyle}>{attempt.query}</div>

                    <div style={labelStyle}>{t('adminDebug.fields.engine')}:</div>
                    <div style={valueStyle}>
                        <span
                            style={{
                                backgroundColor: getEngineColor(attempt.engine),
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '0.9em'
                            }}
                        >
                            {attempt.engine}
                        </span>
                    </div>

                    <div style={labelStyle}>{t('adminDebug.fields.numberOfResults')}:</div>
                    <div style={valueStyle}>{attempt.number_of_results}</div>

                    {attempt.max_results !== undefined && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.maxResults')}:</div>
                            <div style={valueStyle}>{attempt.max_results}</div>
                        </>
                    )}
                </div>
            </div>

            {/* Timestamps */}
            <div style={sectionStyle}>
                <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.timestamps')}</h2>
                <div style={gridStyle}>
                    <div style={labelStyle}>{t('adminDebug.fields.timestamp')}:</div>
                    <div style={valueStyle}>{formatDateTime(attempt.timestamp)}</div>

                    {attempt.insert_timestamp && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.insertTimestamp')}:</div>
                            <div style={valueStyle}>{formatDateTime(attempt.insert_timestamp)}</div>
                        </>
                    )}

                    {attempt.update_timestamp && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.updateTimestamp')}:</div>
                            <div style={valueStyle}>{formatDateTime(attempt.update_timestamp)}</div>
                        </>
                    )}
                </div>
            </div>

            {/* Search Results */}
            {attempt.results && attempt.results.length > 0 && (
                <div style={sectionStyle}>
                    <h2 style={{ marginBottom: '1rem' }}>
                        {t('adminDebug.sections.searchResults')} ({attempt.results.length})
                    </h2>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th>{t('adminDebug.fields.title')}</th>
                                    <th style={{ width: '300px' }}>{t('adminDebug.fields.url')}</th>
                                    <th>{t('adminDebug.fields.snippet')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attempt.results.map((result: ISearchResult, index: number) => (
                                    <tr key={index}>
                                        <td>{result.position ?? index + 1}</td>
                                        <td>{result.title || '-'}</td>
                                        <td style={{ wordBreak: 'break-all' }}>
                                            {result.url ? (
                                                <a
                                                    href={result.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#007bff' }}
                                                >
                                                    {result.url.length > 60 ? result.url.substring(0, 60) + '...' : result.url}
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td title={result.snippet}>
                                            {result.snippet ? (
                                                result.snippet.length > 100 ? result.snippet.substring(0, 100) + '...' : result.snippet
                                            ) : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Raw Response */}
            {attempt.raw_response && (
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2>{t('adminDebug.sections.rawResponse')}</h2>
                        <button
                            onClick={() => setShowRawResponse(!showRawResponse)}
                            style={{
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                backgroundColor: showRawResponse ? '#dc3545' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px'
                            }}
                        >
                            {showRawResponse ? t('adminDebug.hide') : t('adminDebug.show')}
                        </button>
                    </div>
                    {showRawResponse && (
                        <pre
                            style={{
                                backgroundColor: '#1e1e1e',
                                color: '#d4d4d4',
                                padding: '1rem',
                                borderRadius: '4px',
                                overflow: 'auto',
                                maxHeight: '400px',
                                fontSize: '0.85em'
                            }}
                        >
                            {JSON.stringify(attempt.raw_response, null, 2)}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminSearchAttemptDetailView;
